import React from 'react'

import { canView } from './helpers'
import { downloadBlob } from '../../utilities'
import useMediaCache from '@SonarCX/hooks/useMediaCache'

type UseMediaHandleProps = {
  media: Media
  manualEndLoad?: boolean
}

const useMediaHandle = ({
  media,
  manualEndLoad = false
}: UseMediaHandleProps) => {
  const { getMedia, status, media: mediaCache } = useMediaCache()

  const [file, setFile] = React.useState<Blob | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [shouldLoad, setShouldLoad] = React.useState(false)
  const isViewable = React.useMemo(
    () => (media ? canView(media) : false),
    [media]
  )
  const [srcURL, setSrcURL] = React.useState('')
  const [downloadAfterLoad, setDownloadAfterLoad] = React.useState(false)

  const reset = React.useCallback(() => {
    setShouldLoad(false)
    setFile(null)
    setLoading(false)
    setSrcURL('')
  }, [])

  const startLoad = React.useCallback(() => {
    setLoading(true)
    setShouldLoad(true)
  }, [])

  const finishLoad = React.useCallback(() => {
    setLoading(false)
  }, [])

  const download = React.useCallback(() => {
    // Trigger a load and save if the file is not yet downloaded
    if (!file) {
      setDownloadAfterLoad(true)
      startLoad()
      return
    }

    // Normal download case
    downloadBlob(media.filename, file)
  }, [file, startLoad, media.filename])

  // Load media
  React.useEffect(() => {
    if (!media || !shouldLoad || file) {
      return
    }

    setLoading(true)
    if (media.id in mediaCache) {
      // Convert url from cache to blob
      fetch(mediaCache[media.id])
        .then(r => r.blob())
        .then(f => {
          setFile(f)
          setSrcURL(URL.createObjectURL(f))

          if (!manualEndLoad) {
            finishLoad()
          }

          if (downloadAfterLoad) {
            downloadBlob(media.filename, f)
          }
        })
        .catch(() => {
          setLoading(false)
        })
      return
    }

    if (!(media.id in status)) {
      getMedia(media.id)
    }
  }, [
    media,
    shouldLoad,
    file,
    mediaCache,
    manualEndLoad,
    finishLoad,
    status,
    getMedia,
    downloadAfterLoad,
    download
  ])

  return {
    reset,
    startLoad,
    finishLoad,
    download,
    media,
    file,
    loading,
    isViewable,
    srcURL,
    downloadAfterLoad
  }
}

export type UseMediaHandleType = ReturnType<typeof useMediaHandle>

export default useMediaHandle
